import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Layers,
  Database,
  Palette,
  GraduationCap,
  ArrowRight,
  Info,
  Zap,
} from 'lucide-react';
import { usePreviewStore } from '../../../stores/previewStore';
import { useMappingStore } from '../../../stores/mappingStore';
import type { ResolvedElement } from '../../../types/preview';

export default function PreviewInfoPanel() {
  const { resolvedElements, previewMode } = usePreviewStore();
  const { preview } = useMappingStore();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['layers', 'resolution'])
  );

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const stats = useMemo(() => {
    const bgElements = resolvedElements.filter(
      (re) => re.resolved.layer === 'BACKGROUND_LAYER' && re.resolved.visible
    );
    const ovElements = resolvedElements.filter(
      (re) => re.resolved.layer === 'OVERLAY_LAYER' && re.resolved.visible
    );
    const dynamicElements = resolvedElements.filter(
      (re) => re.resolved.contentType === 'DYNAMIC'
    );
    const fullyResolved = dynamicElements.filter((re) => re.fullyResolved);
    const withErrors = dynamicElements.filter((re) => re.errors.length > 0);
    const totalMappings = dynamicElements.reduce(
      (sum, re) => sum + re.appliedMappings.length, 0
    );
    const resolvedFromSource = dynamicElements.reduce(
      (sum, re) => sum + re.appliedMappings.filter((am) => am.fromSource).length, 0
    );

    return {
      bgCount: bgElements.length,
      ovCount: ovElements.length,
      totalElements: resolvedElements.filter((re) => re.resolved.visible).length,
      dynamicCount: dynamicElements.length,
      fullyResolvedCount: fullyResolved.length,
      errorCount: withErrors.length,
      totalMappings,
      resolvedFromSource,
      fallbackCount: totalMappings - resolvedFromSource,
    };
  }, [resolvedElements]);

  return (
    <div className="w-64 border-l border-white/[0.06] bg-[#12121a] flex flex-col overflow-hidden shrink-0">
      <div className="px-3 py-2 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Info className="w-3.5 h-3.5 text-purple-400" />
          <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
            Preview Info
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto sidebar-scroll">
        <SectionHeader
          title="Layer Distribution"
          icon={<Layers className="w-3.5 h-3.5 text-purple-400" />}
          isExpanded={expandedSections.has('layers')}
          onToggle={() => toggleSection('layers')}
        />
        {expandedSections.has('layers') && (
          <div className="px-3 pb-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-xs text-white/60">Background</span>
              </div>
              <span className="text-xs font-mono text-amber-400">{stats.bgCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-500" />
                <span className="text-xs text-white/60">Overlay</span>
              </div>
              <span className="text-xs font-mono text-cyan-400">{stats.ovCount}</span>
            </div>
            <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden flex">
              {stats.totalElements > 0 && (
                <>
                  <div
                    className="h-full bg-amber-500/60 transition-all"
                    style={{ width: `${(stats.bgCount / stats.totalElements) * 100}%` }}
                  />
                  <div
                    className="h-full bg-cyan-500/60 transition-all"
                    style={{ width: `${(stats.ovCount / stats.totalElements) * 100}%` }}
                  />
                </>
              )}
            </div>
            <p className="text-[10px] text-white/30">
              {stats.totalElements} visible element{stats.totalElements !== 1 ? 's' : ''} total
            </p>

            {previewMode === 'background' && (
              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-[10px] text-amber-400">
                  Showing {stats.bgCount} background element{stats.bgCount !== 1 ? 's' : ''} -- these will be rasterized to JPEG
                </p>
              </div>
            )}
            {previewMode === 'overlay' && (
              <div className="p-2 rounded-md bg-cyan-500/10 border border-cyan-500/20">
                <p className="text-[10px] text-cyan-400">
                  Showing {stats.ovCount} overlay element{stats.ovCount !== 1 ? 's' : ''} -- these will be rendered as HTML/CSS divs
                </p>
              </div>
            )}
            {previewMode === 'combined' && (
              <div className="p-2 rounded-md bg-purple-500/10 border border-purple-500/20">
                <p className="text-[10px] text-purple-400">
                  Combined view -- background JPEG + overlay HTML as end users will see it
                </p>
              </div>
            )}
          </div>
        )}

        <SectionHeader
          title="Dynamic Resolution"
          icon={<Database className="w-3.5 h-3.5 text-purple-400" />}
          isExpanded={expandedSections.has('resolution')}
          onToggle={() => toggleSection('resolution')}
          badge={
            stats.dynamicCount > 0
              ? `${stats.fullyResolvedCount}/${stats.dynamicCount}`
              : undefined
          }
        />
        {expandedSections.has('resolution') && (
          <div className="px-3 pb-3 space-y-2">
            {stats.dynamicCount === 0 ? (
              <p className="text-xs text-white/30 italic">
                No dynamic elements in this design
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-md bg-white/[0.04]">
                    <p className="text-[10px] text-white/40">Mappings</p>
                    <p className="text-sm font-mono text-white/80">{stats.totalMappings}</p>
                  </div>
                  <div className="p-2 rounded-md bg-white/[0.04]">
                    <p className="text-[10px] text-white/40">From Source</p>
                    <p className="text-sm font-mono text-emerald-400">{stats.resolvedFromSource}</p>
                  </div>
                  <div className="p-2 rounded-md bg-white/[0.04]">
                    <p className="text-[10px] text-white/40">Fallbacks</p>
                    <p className="text-sm font-mono text-amber-400">{stats.fallbackCount}</p>
                  </div>
                  <div className="p-2 rounded-md bg-white/[0.04]">
                    <p className="text-[10px] text-white/40">Errors</p>
                    <p className={`text-sm font-mono ${stats.errorCount > 0 ? 'text-red-400' : 'text-white/40'}`}>
                      {stats.errorCount}
                    </p>
                  </div>
                </div>

                <div className="space-y-1 mt-2">
                  {resolvedElements
                    .filter((re) => re.resolved.contentType === 'DYNAMIC')
                    .map((re) => (
                      <ResolutionItem key={re.resolved.id} element={re} />
                    ))}
                </div>
              </>
            )}
          </div>
        )}

        <SectionHeader
          title="Data Source"
          icon={<Zap className="w-3.5 h-3.5 text-purple-400" />}
          isExpanded={expandedSections.has('data')}
          onToggle={() => toggleSection('data')}
        />
        {expandedSections.has('data') && (
          <div className="px-3 pb-3 space-y-2">
            {preview ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-2 rounded-md bg-white/[0.04]">
                  {preview.source === 'ad' ? (
                    <Palette className="w-3.5 h-3.5 text-pink-400" />
                  ) : (
                    <GraduationCap className="w-3.5 h-3.5 text-blue-400" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white/70 truncate">{preview.tableName}</p>
                    <p className="text-[10px] text-white/40">
                      Record {preview.selectedRecordIndex + 1} of {preview.records.length}
                    </p>
                  </div>
                </div>
                <p className="text-[10px] text-white/30">
                  Use the Data Preview tab in the mapping panel to change records
                </p>
              </div>
            ) : (
              <div className="p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-400">No preview data loaded</p>
                <p className="text-[10px] text-amber-400/60 mt-1">
                  Open the Dynamic Field Mapping panel and load preview data to see resolved values
                </p>
              </div>
            )}
          </div>
        )}

        <SectionHeader
          title="Shortcuts"
          icon={<span className="text-[10px] text-purple-400 font-mono">KB</span>}
          isExpanded={expandedSections.has('shortcuts')}
          onToggle={() => toggleSection('shortcuts')}
        />
        {expandedSections.has('shortcuts') && (
          <div className="px-3 pb-3">
            <div className="space-y-1">
              {[
                { key: '1', action: 'Background mode' },
                { key: '2', action: 'Overlay mode' },
                { key: '3', action: 'Combined mode' },
                { key: 'B', action: 'Toggle boundaries' },
                { key: 'L', action: 'Toggle layer indicators' },
                { key: 'A', action: 'Toggle ADA panel' },
                { key: '+/-', action: 'Zoom in/out' },
                { key: '0', action: 'Reset zoom' },
                { key: 'Esc', action: 'Exit preview' },
              ].map(({ key, action }) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-xs text-white/40">{action}</span>
                  <kbd className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-white/[0.06] text-white/50 border border-white/[0.08]">
                    {key}
                  </kbd>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  icon,
  isExpanded,
  onToggle,
  badge,
}: {
  title: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  badge?: string;
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-3 py-2 border-b border-white/[0.04] hover:bg-white/[0.04] transition-colors"
    >
      {isExpanded ? (
        <ChevronDown className="w-3 h-3 text-white/40" />
      ) : (
        <ChevronRight className="w-3 h-3 text-white/40" />
      )}
      {icon}
      <span className="text-xs font-semibold text-white/60 uppercase tracking-wider flex-1 text-left">
        {title}
      </span>
      {badge && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-400 font-mono">
          {badge}
        </span>
      )}
    </button>
  );
}

function ResolutionItem({ element }: { element: ResolvedElement }) {
  const [expanded, setExpanded] = useState(false);
  const hasErrors = element.errors.length > 0;
  const allFromSource = element.appliedMappings.every((am) => am.fromSource);

  return (
    <div
      className={`rounded-md border transition-colors ${
        hasErrors
          ? 'bg-red-500/5 border-red-500/20'
          : allFromSource
          ? 'bg-emerald-500/5 border-emerald-500/20'
          : 'bg-amber-500/5 border-amber-500/20'
      }`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-2 py-1.5"
      >
        {hasErrors ? (
          <XCircle className="w-3 h-3 text-red-400 shrink-0" />
        ) : allFromSource ? (
          <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
        ) : (
          <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
        )}
        <span className="text-xs text-white/70 truncate flex-1 text-left">
          {element.resolved.name || element.resolved.type}
        </span>
        <span className="text-[10px] text-white/30">
          {element.appliedMappings.length} mapping{element.appliedMappings.length !== 1 ? 's' : ''}
        </span>
        {expanded ? (
          <ChevronDown className="w-3 h-3 text-white/30" />
        ) : (
          <ChevronRight className="w-3 h-3 text-white/30" />
        )}
      </button>

      {expanded && (
        <div className="px-2 pb-2 space-y-1">
          {element.appliedMappings.map((am, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[10px] text-white/50">
              <span className="truncate">{am.mapping.columnName || am.mapping.targetProperty}</span>
              <ArrowRight className="w-2.5 h-2.5 shrink-0 text-white/20" />
              <span className={`truncate ${am.fromSource ? 'text-emerald-400' : 'text-amber-400'}`}>
                {am.resolvedValue || '(empty)'}
              </span>
            </div>
          ))}
          {element.errors.map((err, i) => (
            <p key={i} className="text-[10px] text-red-400">! {err}</p>
          ))}
        </div>
      )}
    </div>
  );
}
